import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import api from 'utils/api';
import { getApiErrorMessage } from 'utils/apiErrors';
import AddressFormFields from 'components/address/AddressFormFields';
import { emptyAddressFormValues } from 'components/address/mapPlaceToFormValues';
import { isAddressComplete } from 'components/address/addressValidation';

const formatPhone = (input = '') => {
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  let formatted = '';
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};
const isValidPhone = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

const addressFormValues = (address) => ({
  ...emptyAddressFormValues(),
  street: address?.street || '',
  addressLine2: address?.addressLine2 || '',
  city: address?.city || '',
  state: address?.state || '',
  zipCode: address?.zipCode || '',
  countryCode: address?.countryCode || '',
  countryName: address?.countryName || '',
  latitude: address?.latitude ?? null,
  longitude: address?.longitude ?? null,
  phone: formatPhone(address?.phone || address?.phoneNumber || ''),
});

const AddOrEditAddressDialog = ({ open, onClose, onAddressAdded, businessId: selectedBusinessId, address, sx, searchPopupZIndex }) => {
  const { enqueueSnackbar } = useSnackbar();
  const currentBusinessId = useSelector(
    (state) => state.business.businessId || state.business.companyInfo?.businessId
  );
  const businessId = selectedBusinessId ?? currentBusinessId;

  const [newAddress, setNewAddress] = useState(() => addressFormValues(address));
  const [saving, setSaving] = useState(false);
  const editing = address?.addressId != null;

  useEffect(() => {
    if (open) {
      setNewAddress(addressFormValues(address));
    }
  }, [address, open]);

  const handleClose = () => {
    setNewAddress(addressFormValues());
    onClose();
  };

  const handleSave = () => {
    if (saving) return;
    const isPhoneValid = !newAddress.phone || isValidPhone(newAddress.phone);
    if (!isAddressComplete(newAddress) || !isPhoneValid) {
      if (!isPhoneValid) {
        enqueueSnackbar('Phone must be blank or in format (212) 555-1234', { variant: 'warning' });
      } else {
        enqueueSnackbar('Please fill out all required address fields.', { variant: 'warning' });
      }
      return;
    }

    const { phone, ...mailing } = newAddress;
    const data = { ...mailing, phone: phone || '' };

    setSaving(true);
    const request = editing
      ? api.put(`/business/${businessId}/addresses/${address.addressId}`, data)
      : api.post(`/business/${businessId}/addresses`, data);
    request
      .then((savedAddress) => {
        enqueueSnackbar(editing ? 'Address updated successfully.' : 'Address added successfully.', { variant: 'success' });
        onAddressAdded?.(savedAddress);
        handleClose();
      })
      .catch((error) => {
        enqueueSnackbar(getApiErrorMessage(error, editing
          ? 'Couldn’t update this address. Check the fields and try again.'
          : 'Couldn’t add this address. Check the fields and try again.'), { variant: 'error' });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={sx}>
      <Box position="relative">
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>{editing ? 'Edit address' : 'Add a new address'}</DialogTitle>
        <DialogContent>
          <AddressFormFields
            values={newAddress}
            onChange={(patch) => setNewAddress((prev) => ({ ...prev, ...patch }))}
            searchLabel="Search offer business address"
            searchPopupZIndex={searchPopupZIndex}
          />
          <TextField
            fullWidth
            label="Phone number for this address (optional)"
            value={newAddress.phone}
            onChange={(e) => setNewAddress({ ...newAddress, phone: formatPhone(e.target.value) })}
            margin="normal"
            error={Boolean(newAddress.phone) && !isValidPhone(newAddress.phone)}
            helperText={
              <>
                {Boolean(newAddress.phone) && !isValidPhone(newAddress.phone) && 'Phone format: (212) 555-1234'}
                <span style={{ display: 'block' }}>Saved with this address so its offer can show a Call button.</span>
              </>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} color="primary" disabled={saving}>
            {editing ? 'Save changes' : 'Save address'}
          </Button>
          <Button onClick={handleClose} color="error">
            Cancel
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddOrEditAddressDialog;
